<template>
  <div>
    <transition mode="out-in" name="fade">
      <template v-if="loading">
        <div :class="classes.loader" />
      </template>
      <template v-else-if="!error && !loading && hasEvents">
        <App />
      </template>
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watchEffect, computed, Ref } from "vue";
import { setupContext, DebugContext } from "@fresh8/debug-kit/src/debugContext";
import { useLocalstorageRef } from "@fresh8/vue-utils/src/useLocalStorageRef";

import { adtag, bet } from "./prefetch";
import { provideBetData, provideAdTag } from "./lib/provideInjectors";
import { provideOffer } from "./composition/offer";
import { BetData } from "./types/betData";
import { AdTag } from "./types";
import { provideBrand } from "./composition/brand";
import { useHasEvents } from "./hooks/useHasEvents";
import { preloadImages } from "./lib/preloadImages";

// simple wrapper to enable HMR
import App from "./App.vue";
import { isTouchDevice, provideTouchDevice } from "./lib/isTouchDevice";
import {
  createJssContext,
  provideJssContext,
  createUseStyles
} from "@fresh8/vue-jss/src";
import {
  provideAnalyticsContext,
  createAnalyticsContext
} from "./lib/analytics";

const useStyles = createUseStyles({
  loader: {
    border: "5px solid #f3f3f3",
    borderTop: "5px solid #acacac",
    borderRadius: "50%",
    width: "20px",
    height: "20px",
    animationName: "$spin",
    animationDuration: "0.5s",
    animationIterationCount: "infinite",
    animationTimingFunction: "linear",
    position: "absolute",
    margin: "auto",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0
  },

  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" }
  }
});

function awaitPromise<T>(promise: Promise<T>) {
  const result = ref<T | undefined>();
  const loading = ref(true);
  const error = ref(false);

  watchEffect(async () => {
    try {
      result.value = await promise;
      loading.value = false;
    } catch (e) {
      loading.value = false;
      error.value = true;
    }
  });

  return { result, loading, error };
}

function useStuff({
  adTag,
  adTagLoading,
  betData,
  betDataLoading,
  debugContext
}: {
  adTag: Ref<AdTag>;
  adTagLoading: Ref<boolean>;
  betData: Ref<BetData>;
  betDataLoading: Ref<boolean>;
  debugContext: DebugContext;
}) {
  const viewIDRef = ref();
  let localStorageDataRef;

  watchEffect(() => {
    if (
      viewIDRef.value &&
      debugContext.enableCapture.value &&
      !localStorageDataRef
    ) {
      localStorageDataRef = useLocalstorageRef(
        `captured-id-${viewIDRef.value}`,
        Object,
        {} // shouldn't be used as it should already be set by the watch effect below
      );
    }

    if (localStorageDataRef && localStorageDataRef.value) {
      adTag.value = localStorageDataRef.value.adTagData;
      betData.value = localStorageDataRef.value.betData;
    }
  });

  watchEffect(() => {
    if (adTagLoading.value === false && betDataLoading.value === false) {
      if (debugContext.enableCapture.value) {
        const viewID = betData.value.analytics.viewId;
        viewIDRef.value = viewID;
        debugContext.setEvent(viewID, {
          viewID,
          adTagData: adTag.value,
          betData: betData.value,
          time: Date.now().toString()
        });
      }
    }
  });
}

export default defineComponent({
  name: "AppWrapper",
  components: {
    App
  },
  setup() {
    const {
      result: adTag,
      loading: adTagLoading,
      error: adTagError
    } = awaitPromise<AdTag>(adtag);

    const {
      result: betData,
      loading: betDataLoading,
      error: betDataError
    } = awaitPromise<BetData>(bet);
    provideOffer(betData);
    const debugContext = setupContext();

    const jssContext = createJssContext({});
    const classesRef = useStyles();
    const analyticsContext = createAnalyticsContext(
      betData,
      debugContext.enableAnalytics
    );
    preloadImages();
    provideJssContext(jssContext);
    provideBetData(betData);
    provideAdTag(adTag);
    provideBrand(adTag);
    provideAnalyticsContext(analyticsContext);
    provideTouchDevice(ref(isTouchDevice()));

    watchEffect(() => {
      if (adTagError.value || betDataError.value) {
        window.parent.postMessage({ type: "f8_failed_loading" }, "*");
        document.documentElement.style.height = "0px";
        document.documentElement.style.overflow = "hidden";
        document.body.style.height = "0px";
        document.body.style.overflow = "hidden";
      }
    });

    const { hasEvents } = useHasEvents(betDataLoading);

    useStuff({
      adTag,
      adTagLoading,
      betData,
      betDataLoading,
      debugContext
    });

    return {
      loading: adTagLoading || betDataLoading,
      classes: classesRef,
      error: computed(() => adTagError.value || betDataError.value),
      hasEvents
    };
  }
});
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
